/**
 * Chrome for simulation mode. Mirrors PracticeShell's shape (header / scroll /
 * progress footer) but labels the mode for exam-style sessions: "Simulation"
 * in the header, "Question X of Y" in the footer. No mode switching here —
 * simulation is its own thing, not a SessionMode.
 *
 * Differs from PracticeShell intentionally:
 *   - Header carries the pass threshold next to the title so the user can see
 *     it without leaving the test.
 *   - The progress bar caps to total questions, not session length.
 */
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

type Props = {
  onQuit: () => void;
  current: number; // 1-based
  total: number;
  passThreshold?: number;
  children: ReactNode;
};

export function SimulationShell({ onQuit, current, total, passThreshold, children }: Props) {
  const pct = total > 0 ? (current / total) * 100 : 0;
  return (
    <div className="bg-background flex min-h-screen flex-col">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={onQuit}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
            Quit
          </Button>
          <div className="flex items-baseline gap-2 text-sm">
            <span className="text-muted-foreground font-medium">Simulation</span>
            {passThreshold ? (
              <span className="text-muted-foreground text-xs">
                · pass at {passThreshold}/{total}
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto flex max-w-3xl flex-col px-4 py-6 sm:px-6 sm:py-10">{children}</div>
      </main>

      <footer className="border-border bg-card border-t">
        <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4 py-3 sm:px-6">
          <div className="text-muted-foreground text-xs font-medium">
            Question {current} of {total}
          </div>
          <Progress value={pct} />
        </div>
      </footer>
    </div>
  );
}
